import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router';
import { graphql } from '@apollo/react-hoc';
import gql from 'graphql-tag';
import isEmpty from 'lodash/isEmpty';

import HasRole from '../../../../components/HasRole';
import PremiumHemSubscription from '../../../../components/PremiumHemSubscription';
import Header from '../../../../components/Header';
import Button from '../../../../components/Button';
import Dropdown from '../../../../components/Dropdown';
import Icon from '../../../../components/Icon';

import LockedContentComponent from './LockedContent';
import { SimpleLoadingComponent } from '../../../../utils/apollo';
import { addHttpToUrl, isUrlValid } from '../../../../utils/url';
import * as constants from '../../../Authorization/constants';
import * as actions from '../../actions';
import ProfileCard from './profileCard';
import messages from '../../messages';
import '../../styles.css';

const LinksAndProfilesBody = (
  <div><strong>Links</strong> allow you to provide students direct access to &quot;Communicate&quot;
    with your institution, &quot;Apply Online&quot; using your preferred application, as well as visit up to 10 custom links.{'  '}
    <strong>Spotlights</strong> are an opportunity to highlight your current students, alumni, faculty and academic programs so
    students can gain further insight into your institution.
  </div>
);

class LinksAndProfiles extends React.PureComponent {
  static propTypes = {
    toggleUpgradeFormVisibility: PropTypes.func.isRequired,
    requestInformationLink: PropTypes.object,
    applyOnlineLink: PropTypes.object,
    learnMoreLinks: PropTypes.array,
    initializeCustomProfiles: PropTypes.func.isRequired,
    hasRegisteredChanges: PropTypes.bool.isRequired,
    customProfiles: PropTypes.array.isRequired,
    scannedPhotos: PropTypes.object,
  };

  static defaultProps = {
    learnMoreLinks: [],
  };

  componentDidMount = () => {
    const { hasRegisteredChanges, initializeCustomProfiles, customProfiles } = this.props;
    const hubsProfiles = get(this.props, 'viewer.hubsProfiles.custom_profiles', []);

    if (isEmpty(customProfiles) && !hasRegisteredChanges && hubsProfiles) {
      initializeCustomProfiles({
        customProfiles: cloneDeep(hubsProfiles),
        hasRegisteredChanges: false,
        areNewProfiles: false,
      });
    }
  };

  render() {
    const { toggleUpgradeFormVisibility, requestInformationLink, applyOnlineLink, learnMoreLinks, scannedPhotos } = this.props;
    const customProfiles = this.props.customProfiles || [];

    const alumniProfiles = customProfiles.length > 0 ? customProfiles.filter((p) => p.type === 'alumni') : [];
    const studentProfiles = customProfiles.length > 0 ? customProfiles.filter((p) => p.type === 'student') : [];
    const programProfiles = customProfiles.length > 0 ? customProfiles.filter((p) => p.type === 'program') : [];
    const facultyProfiles = customProfiles.length > 0 ? customProfiles.filter((p) => p.type === 'faculty') : [];
    return (
      <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
        <PremiumHemSubscription>
          <Header as="h1" styleName="links-and-profiles-header">
            <FormattedMessage {...messages.linksAndProfilesHeader} />
          </Header>
          <Header.Subheader>
            <FormattedMessage {...messages.linksAndProfilesSubheader} />
          </Header.Subheader><br />
          <div styleName="content-section">
            <Header as="h2" styleName="links-header">
              <FormattedMessage {...messages.linksHeader} />
            </Header>
            <Header.Subheader>
              <FormattedMessage {...messages.linksSubheader} />
            </Header.Subheader><br />
          </div>

          <Dropdown
            id="request-info-dropdown"
            floating
            button
            trigger={<span>Communicate <Icon name="chevron down" color="grey" /></span>}
            icon={null}
            styleName="links-button"
            disabled={!requestInformationLink || !isUrlValid(requestInformationLink.url) || requestInformationLink.url === ''}
          >
            <Dropdown.Menu>
              {requestInformationLink && requestInformationLink.url && isUrlValid(requestInformationLink.url) && (
                <Dropdown.Item>
                  <Link
                    data-cy="Premium-Hem-Request-Information-Link"
                    to={addHttpToUrl(requestInformationLink.url)}
                    target="_blank"
                  >
                    Request Information
                  </Link>
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            id="apply-online-link"
            disabled={!applyOnlineLink || !isUrlValid(applyOnlineLink.url) || applyOnlineLink.url === ''}
            styleName="links-button"
          >
            <span>
              {applyOnlineLink && applyOnlineLink.url ?
                <Link to={addHttpToUrl(applyOnlineLink.url)} target="_blank">Apply Online</Link> : <span>Apply Online</span>}
            </span>
          </Button>

          <Dropdown
            id="learn-more-dropdown"
            floating
            button
            trigger={<span>Learn More <Icon name="chevron down" color="grey" /></span>}
            icon={null}
            styleName="links-button"
            disabled={learnMoreLinks.length === 0}
          >
            <Dropdown.Menu>
              {learnMoreLinks.map((link) => (
                <Dropdown.Item key={link.order}>
                  <Link to={addHttpToUrl(link.url)} target="_blank" key={link.order}>{link.topic}</Link>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <div styleName="content-section profiles">
            <Header as="h2" styleName="profiles-header">
              <FormattedMessage {...messages.profilesHeader} />
            </Header>
            { (!customProfiles || !customProfiles.length) && (
              <Header.Subheader>
                <FormattedMessage
                  {...messages.profilesEmptySubheader}
                  values={{
                    i: (...chunks) => <em>{chunks}</em>,
                  }}
                />
              </Header.Subheader>
            )}
          </div>

          <div className="custom-profiles">
            { alumniProfiles.length > 0 && (
              <div data-cy="Premium-Hem-Profiles-Alumni">
                <Header as="h3" styleName="custom-profiles-header">Alumni Spotlights</Header>
                { alumniProfiles.map((profile, index) => (
                  <ProfileCard
                    key={profile.product_id}
                    profile={profile}
                    imgDisplayUrl={scannedPhotos[customProfiles.indexOf(profile)] || profile.img}
                  />
                ))}
              </div>
            )}
            { facultyProfiles.length > 0 && (
              <div data-cy="Premium-Hem-Profiles-Faculty">
                <Header as="h3" styleName="custom-profiles-header">Faculty Spotlights</Header>
                { facultyProfiles.map((profile, index) => (
                  <ProfileCard
                    key={profile.product_id}
                    profile={profile}
                    imgDisplayUrl={scannedPhotos[customProfiles.indexOf(profile)] || profile.img}
                  />
                ))}
              </div>
            )}
            { programProfiles.length > 0 && (
              <div data-cy="Premium-Hem-Profiles-Program">
                <Header as="h3" styleName="custom-profiles-header">Program Spotlights</Header>
                { programProfiles.map((profile, index) => (
                  <ProfileCard
                    key={profile.product_id}
                    profile={profile}
                    imgDisplayUrl={scannedPhotos[customProfiles.indexOf(profile)] || profile.img}
                  />
                ))}
              </div>
            )}
            { studentProfiles.length > 0 && (
              <div data-cy="Premium-Hem-Profiles-Student">
                <Header as="h3" styleName="custom-profiles-header">Student Spotlights</Header>
                { studentProfiles.map((profile, index) => (
                  <ProfileCard
                    key={profile.product_id}
                    profile={profile}
                    imgDisplayUrl={scannedPhotos[customProfiles.indexOf(profile)] || profile.img}
                  />
                ))}
              </div>
            )}
          </div>
        </PremiumHemSubscription>
        <PremiumHemSubscription inactive>
          <LockedContentComponent
            header="Unlock Links and Spotlights"
            subheader="Give students more opportunities to learn about your institution."
            body={LinksAndProfilesBody}
            toggleUpgradeFormVisibility={toggleUpgradeFormVisibility}
          />
        </PremiumHemSubscription>
      </HasRole>
    );
  }
}

export const LinksAndProfilesComponent = injectIntl(LinksAndProfiles);

const query = gql`
  query LinksAndProfiles {
    viewer {
      id
      heInstitution
      hubsProfiles {
        custom_profiles {
          img
          img_thumb_large
          type
          logo
          body
          headline
          quote
          best_place_to_eat
          class
          department
          desc
          fav_activities
          fav_event
          fav_hangout
          fav_tradition
          hometown
          major
          persons_name
          program_name
          travel
          add_facts
          imgDisplayUrl
          logoDisplayUrl
          product_id
        }
      }
    }
  }
`;

const mapStateToProps = (state) => ({
  requestInformationLink: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.requestInformationLink'),
  applyOnlineLink: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.applyOnlineLink'),
  learnMoreLinks: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.learnMoreLinks'),
  hasRegisteredChanges: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.hasRegisteredChanges'),
  customProfiles: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.customProfiles'),
  scannedPhotos: get(state, 'nonPersistedCollegeProfile.linksAndProfiles.draftImagesData.scannedPhotoUrls', {}),
});
export default compose(
  connect(mapStateToProps, { ...actions }),
  SimpleLoadingComponent(graphql(query)),
)(LinksAndProfilesComponent);
